import { useState, useMemo, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { products, categories } from '../../mocks/products';
import { useCart } from '../../hooks/useCart';
import { useProductImages } from '../../hooks/useProductImages';

export default function ProductsPage() {
  const { addItem } = useCart();
  const { getImage, updateImage, getPrice, getName, updateData } = useProductImages();

  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [added, setAdded] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingImageId, setEditingImageId] = useState<string | null>(null);
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [tempPrice, setTempPrice] = useState('');
  const [tempName, setTempName] = useState('');

  const filtered = useMemo(() => {
    let list = [...products];
    if (activeCategory !== 'all') list = list.filter(p => p.category === activeCategory);
    if (search) list = list.filter(p => p.name.includes(search) || p.description.includes(search));
    if (sortBy === 'price-asc') list.sort((a, b) => getPrice(a.id, a.price) - getPrice(b.id, b.price));
    if (sortBy === 'price-desc') list.sort((a, b) => getPrice(b.id, b.price) - getPrice(a.id, a.price));
    if (sortBy === 'rating') list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [activeCategory, sortBy, search, getPrice]);

  const handleAdd = (product: typeof products[0]) => {
    const price = getPrice(product.id, product.price);
    const name = getName(product.id, product.name);
    const image = getImage(product.id, product.image);
    addItem({ id: product.id, name, price, image });
    setAdded(product.id);
    setTimeout(() => setAdded(null), 1500);
  };

  const handleImageClick = (productId: string) => {
    if (!editMode) return;
    setEditingImageId(productId);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingImageId) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const result = ev.target?.result as string;
      if (result) updateImage(editingImageId, result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
    setEditingImageId(null);
  };

  const startEditPrice = (productId: string, currentPrice: number) => {
    setEditingPriceId(productId);
    setTempPrice(String(currentPrice));
  };

  const savePrice = (productId: string) => {
    const num = parseFloat(tempPrice);
    if (!isNaN(num) && num > 0) {
      updateData(productId, { price: num });
      showSaved();
    }
    setEditingPriceId(null);
  };

  const startEditName = (productId: string, currentName: string) => {
    setEditingNameId(productId);
    setTempName(currentName);
  };

  const saveName = (productId: string) => {
    if (tempName.trim()) {
      updateData(productId, { name: tempName.trim() });
      showSaved();
    }
    setEditingNameId(null);
  };

  const showSaved = () => {
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2000);
  };

  return (
    <main className="min-h-screen bg-[#080808]">
      <Navbar />

      {/* Saved Toast */}
      {savedMsg && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#C9A961] text-black text-sm font-bold px-6 py-3 flex items-center gap-2" style={{ clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)' }}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-check-line" />
          </div>
          تم الحفظ بنجاح
        </div>
      )}

      {/* Hidden File Input */}
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

      {/* Cinematic Page Hero */}
      <section className="relative pt-40 pb-20 px-6 lg:px-12 overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/68ed4ce696c915cdd87c8008f2e1fe58.jpg"
            alt="hero"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/60 via-[#080808]/40 to-[#080808]" />
          {/* Spotlight effect */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#C9A961]/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto flex items-end justify-between gap-4 flex-wrap">
          <div>
            {/* Cinematic label */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#C9A961]" />
              <span className="text-[#C9A961] text-xs font-bold tracking-[0.3em] uppercase">المجموعة الكاملة</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-black text-white mb-3 leading-tight">
              جميع <span className="text-[#C9A961]">المنتجات</span>
            </h1>
            <p className="text-white/50 text-base">اكتشف مجموعتنا الكاملة من العطور والأزياء الفاخرة</p>
          </div>
          <button
            onClick={() => setEditMode(v => !v)}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              editMode
                ? 'bg-[#C9A961] text-black'
                : 'bg-transparent text-[#C9A961] border border-[#C9A961]/40 hover:border-[#C9A961]'
            }`}
            style={{ clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)' }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={editMode ? 'ri-check-double-line' : 'ri-edit-2-line'} />
            </div>
            {editMode ? 'إنهاء التعديل' : 'تعديل المنتجات'}
          </button>
        </div>
      </section>

      {/* Edit Mode Banner */}
      {editMode && (
        <div className="border-y border-[#C9A961]/20 bg-[#C9A961]/5 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-6 text-sm text-[#C9A961]/80 font-medium flex-wrap">
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-camera-line" /></div>
              اضغط على الصورة لتغييرها
            </span>
            <span className="text-[#C9A961]/30">|</span>
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-price-tag-3-line" /></div>
              اضغط على السعر لتعديله
            </span>
            <span className="text-[#C9A961]/30">|</span>
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-text" /></div>
              اضغط على الاسم لتعديله
            </span>
          </div>
        </div>
      )}

      <section className="py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-4 mb-8">
            {/* Search */}
            <div className="relative flex-1 min-w-64">
              <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line text-[#C9A961]/60 text-sm" />
              </div>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="ابحث عن منتج..."
                className="w-full pr-11 pl-4 py-3 bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#C9A961]/50 transition-colors text-right"
                style={{ clipPath: 'polygon(6px 0%, 100% 0%, calc(100% - 6px) 100%, 0% 100%)' }}
              />
            </div>
            {/* Sort */}
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="px-5 py-3 bg-white/5 border border-white/10 text-white/70 text-sm focus:outline-none focus:border-[#C9A961]/50 cursor-pointer transition-colors"
              style={{ clipPath: 'polygon(6px 0%, 100% 0%, calc(100% - 6px) 100%, 0% 100%)' }}
            >
              <option value="default" className="bg-[#111]">الترتيب الافتراضي</option>
              <option value="price-asc" className="bg-[#111]">السعر: من الأقل</option>
              <option value="price-desc" className="bg-[#111]">السعر: من الأعلى</option>
              <option value="rating" className="bg-[#111]">الأعلى تقييماً</option>
            </select>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-3 mb-10">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-5 py-2 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                activeCategory === 'all'
                  ? 'bg-[#C9A961] text-black border-[#C9A961]'
                  : 'bg-transparent text-white/50 border-white/10 hover:border-[#C9A961]/40 hover:text-white/80'
              }`}
              style={{ clipPath: 'polygon(6px 0%, 100% 0%, calc(100% - 6px) 100%, 0% 100%)' }}
            >
              الكل ({products.length})
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                  activeCategory === cat.id
                    ? 'bg-[#C9A961] text-black border-[#C9A961]'
                    : 'bg-transparent text-white/50 border-white/10 hover:border-[#C9A961]/40 hover:text-white/80'
                }`}
                style={{ clipPath: 'polygon(6px 0%, 100% 0%, calc(100% - 6px) 100%, 0% 100%)' }}
              >
                {cat.name} ({products.filter(p => p.category === cat.id).length})
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-10">
            <div className="flex-1 h-px bg-white/5" />
            <span className="text-white/20 text-xs tracking-widest uppercase">{filtered.length} منتج</span>
            <div className="flex-1 h-px bg-white/5" />
          </div>

          {/* Products Grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-32">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 border border-white/10">
                <i className="ri-search-2-line text-4xl text-white/20" />
              </div>
              <p className="text-white/30 text-lg">لا توجد منتجات مطابقة</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
              {filtered.map(product => {
                const displayImage = getImage(product.id, product.image);
                const displayPrice = getPrice(product.id, product.price);
                const displayName = getName(product.id, product.name);

                return (
                  <div
                    key={product.id}
                    className={`group relative bg-[#0e0e0e] overflow-hidden transition-all duration-500 hover:-translate-y-1 ${
                      editMode ? 'border border-[#C9A961]/30' : 'border border-white/5 hover:border-[#C9A961]/30'
                    }`}
                  >
                    {/* Glow on hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                      style={{ boxShadow: 'inset 0 0 40px rgba(201,169,97,0.04)' }} />

                    {/* Image */}
                    <div
                      className={`relative w-full h-72 overflow-hidden bg-[#0a0a0a] ${editMode ? 'cursor-pointer' : ''}`}
                      onClick={() => handleImageClick(product.id)}
                    >
                      <img
                        src={displayImage}
                        alt={displayName}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      {/* Cinematic overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e0e] via-transparent to-transparent opacity-80" />

                      {editMode && (
                        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-12 h-12 border border-[#C9A961] flex items-center justify-center mb-2">
                            <i className="ri-camera-line text-[#C9A961] text-xl" />
                          </div>
                          <span className="text-white/80 text-xs font-bold tracking-widest uppercase">تغيير الصورة</span>
                        </div>
                      )}

                      {product.badge && (
                        <span
                          className="absolute top-3 right-3 bg-[#C9A961] text-black text-xs font-black px-3 py-1 tracking-wider"
                          style={{ clipPath: 'polygon(4px 0%, 100% 0%, calc(100% - 4px) 100%, 0% 100%)' }}
                        >
                          {product.badge}
                        </span>
                      )}

                      {/* Category label top-left */}
                      <div className="absolute top-3 left-3">
                        <span className="text-white/40 text-xs tracking-widest uppercase">
                          {categories.find(c => c.id === product.category)?.name}
                        </span>
                      </div>
                    </div>

                    <div className="p-4">
                      {/* Editable Name */}
                      {editMode && editingNameId === product.id ? (
                        <input
                          autoFocus
                          value={tempName}
                          onChange={e => setTempName(e.target.value)}
                          onBlur={() => saveName(product.id)}
                          onKeyDown={e => { if (e.key === 'Enter') saveName(product.id); if (e.key === 'Escape') setEditingNameId(null); }}
                          className="w-full border-b border-[#C9A961] bg-transparent text-white font-bold text-base mb-1 focus:outline-none text-right"
                        />
                      ) : (
                        <h3
                          className={`text-white font-bold text-base mb-1 truncate transition-colors ${editMode ? 'cursor-pointer hover:text-[#C9A961]' : 'group-hover:text-[#C9A961]'}`}
                          onClick={() => editMode && startEditName(product.id, displayName)}
                          title={editMode ? 'اضغط لتعديل الاسم' : undefined}
                        >
                          {displayName}
                          {editMode && (
                            <span className="inline-flex items-center justify-center w-3 h-3 mr-1">
                              <i className="ri-edit-line text-[#C9A961] text-xs" />
                            </span>
                          )}
                        </h3>
                      )}

                      <p className="text-white/30 text-xs mb-3 truncate">{product.description}</p>

                      {/* Stars */}
                      <div className="flex items-center gap-1 mb-4">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="w-3 h-3 flex items-center justify-center">
                            {i < Math.floor(product.rating) ? (
                              <i className="ri-star-fill text-[#C9A961] text-xs" />
                            ) : (
                              <i className="ri-star-line text-[#C9A961] text-xs" />
                            )}
                          </div>
                        ))}
                        <span className="text-white/25 text-xs mr-1">({product.reviews})</span>
                      </div>

                      {/* Price row */}
                      <div className="flex items-center justify-between mb-4">
                        {editMode && editingPriceId === product.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              autoFocus
                              type="number"
                              value={tempPrice}
                              onChange={e => setTempPrice(e.target.value)}
                              onBlur={() => savePrice(product.id)}
                              onKeyDown={e => { if (e.key === 'Enter') savePrice(product.id); if (e.key === 'Escape') setEditingPriceId(null); }}
                              className="w-20 border-b border-[#C9A961] bg-transparent text-[#C9A961] font-black text-xl focus:outline-none text-right"
                            />
                            <span className="text-white/40 text-xs">د.إ</span>
                          </div>
                        ) : (
                          <div
                            className={`flex items-center gap-1.5 ${editMode ? 'cursor-pointer' : ''}`}
                            onClick={() => editMode && startEditPrice(product.id, displayPrice)}
                            title={editMode ? 'اضغط لتعديل السعر' : undefined}
                          >
                            <span className="text-[#C9A961] font-black text-xl">{displayPrice}</span>
                            <span className="text-white/30 text-xs">د.إ</span>
                            {product.oldPrice && !editMode && (
                              <span className="text-white/20 text-xs line-through mr-1">{product.oldPrice}</span>
                            )}
                            {editMode && (
                              <span className="flex items-center justify-center w-4 h-4">
                                <i className="ri-edit-line text-[#C9A961] text-xs opacity-60" />
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Add to cart button */}
                      <button
                        onClick={() => !editMode && handleAdd(product)}
                        className={`w-full py-3 text-sm font-bold transition-all duration-300 whitespace-nowrap tracking-wider ${
                          editMode
                            ? 'bg-white/5 text-white/20 cursor-not-allowed border border-white/5'
                            : added === product.id
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30 cursor-pointer'
                            : 'bg-transparent text-[#C9A961] border border-[#C9A961]/40 hover:bg-[#C9A961] hover:text-black cursor-pointer'
                        }`}
                        style={{ clipPath: 'polygon(6px 0%, 100% 0%, calc(100% - 6px) 100%, 0% 100%)' }}
                      >
                        {editMode ? 'وضع التعديل' : added === product.id ? '✓ تمت الإضافة' : 'أضف للسلة'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Bottom cinematic divider */}
          <div className="flex items-center gap-4 mt-16">
            <div className="flex-1 h-px bg-white/5" />
            <div className="w-1.5 h-1.5 bg-[#C9A961]/40 rotate-45" />
            <div className="flex-1 h-px bg-white/5" />
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
